import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { AuthView, CustomerView, GuestContactRequest, LoginRequest, StoredCustomer, UpdateFiscalAddressRequest, UpdateProfileRequest } from '../domain/customer.model';
import { CUSTOMER_SESSION_LISTENER } from '../customer-auth.tokens';
import { CustomerAuthApi } from './customer-auth.api';
import {
  clearStoredCustomer, readStoredCustomer, toStoredCustomer, updateStoredContactEmail, writeStoredCustomer
} from './customer-session.store';

/** Resultado de autenticar: el reto de 2FA si lo hay, o la sesión abierta con los avisos de la adopción del carrito. */
export interface LoginOutcome {
  auth: AuthView;
  avisos: string[];
  /** Solo con segundo factor pendiente: lo capturado del carrito, para adoptarlo al completar el reto. */
  captura?: unknown;
}

/**
 * Dueño de la sesión del comprador de la tienda. Las cookies `cu_*` son httpOnly: «¿hay sesión?» se responde con
 * el marcador local (`customer-session.store`), que se fija al crear la sesión y se renueva en cada refresh.
 *
 * El refresh es single-flight: varias peticiones con 401 a la vez comparten una sola renovación (el refresh
 * token rota y un segundo envío en paralelo se leería como reuso y mataría la sesión).
 */
@Injectable({ providedIn: 'root' })
export class CustomerSessionService {
  private readonly api = inject(CustomerAuthApi);
  private readonly listener = inject(CUSTOMER_SESSION_LISTENER, { optional: true });

  private readonly customerSignal = signal<StoredCustomer | null>(readStoredCustomer());
  readonly customer = this.customerSignal.asReadonly();
  readonly hasSession = computed(() => this.customerSignal() !== null);
  readonly isGuest = computed(() => this.customerSignal()?.isGuest === true);
  readonly profileType = computed(() => this.customerSignal()?.profileType ?? null);
  /** Comprador empresa (RUC): el único que ve ofertas y condiciones B2B. El servidor lo vuelve a comprobar en cada consulta. */
  readonly isBusiness = computed(() => this.customerSignal()?.profileType === 'BUSINESS');
  readonly emailVerified = computed(() => this.customerSignal()?.emailVerified === true);

  private guestInFlight: Observable<void> | null = null;
  private refreshInFlight: Observable<AuthView> | null = null;
  private restoreInFlight: Observable<boolean> | null = null;
  private restoreAttempted = false;

  /** Garantiza sesión de tienda, acuñando una de invitado si no hay. Una sola petición en vuelo. */
  asegurarSesion(): Observable<void> {
    if (this.hasSession()) {
      return of(undefined);
    }
    if (this.guestInFlight) {
      return this.guestInFlight;
    }
    this.guestInFlight = this.restaurarSesionSiExiste().pipe(
      switchMap(recuperada => recuperada ? of(undefined) : this.iniciarSesionInvitado().pipe(map(() => undefined))),
      finalize(() => (this.guestInFlight = null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.guestInFlight;
  }

  /** Recupera cookies `cu_*` vigentes aunque falte el marcador local. No crea invitado. */
  restaurarSesionSiExiste(): Observable<boolean> {
    if (this.hasSession()) {
      return of(true);
    }
    if (this.restoreInFlight) {
      return this.restoreInFlight;
    }
    if (this.restoreAttempted) {
      return of(false);
    }
    this.restoreAttempted = true;
    this.restoreInFlight = this.refresh().pipe(
      map(() => true),
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.clearLocalSession();
          return of(false);
        }
        // Un fallo de red o de servidor no prueba que la cookie haya vencido: se permite reintentar.
        this.restoreAttempted = false;
        return throwError(() => error);
      }),
      finalize(() => (this.restoreInFlight = null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.restoreInFlight;
  }

  login(request: LoginRequest): Observable<LoginOutcome> {
    const captura = this.listener?.capturar?.();
    return this.api.login(request).pipe(switchMap(auth => this.abrirSesion(auth, captura)));
  }

  completarSegundoFactor(body: { challengeToken: string; code: string; method: 'TOTP' | 'EMAIL' | 'RECOVERY'; rememberDevice?: boolean }, captura?: unknown): Observable<LoginOutcome> {
    return this.api.verifyTwoFactor(body).pipe(switchMap(auth => this.abrirSesion(auth, captura)));
  }

  /** Captura lo que la tienda quiere conservar de la sesión de invitado antes de iniciar sesión (carrito). */
  capturar(): unknown {
    return this.listener?.capturar?.();
  }

  /** Relee el perfil (p. ej. tras verificar el correo en otra pestaña) y actualiza señal y marcador local. */
  refreshProfile(): Observable<CustomerView> {
    return this.api.me().pipe(tap(customer => this.guardar(customer)));
  }

  /** Perfil editado: se refleja en la sesión sin otra petición. */
  actualizarPerfil(request: UpdateProfileRequest): Observable<CustomerView> {
    return this.api.updateProfile(request).pipe(tap(customer => this.guardar(customer)));
  }

  /** Domicilio fiscal de la empresa editado: se refleja en la sesión para que el checkout lo precargue. */
  actualizarDomicilioFiscal(request: UpdateFiscalAddressRequest): Observable<CustomerView> {
    return this.api.updateFiscalAddress(request).pipe(tap(customer => this.guardar(customer)));
  }

  iniciarSesionInvitado(): Observable<AuthView> {
    return this.api.guest().pipe(tap(auth => this.guardar(auth.customer)));
  }

  actualizarContactoInvitado(request: GuestContactRequest): Observable<CustomerView> {
    return this.api.updateGuestContact(request).pipe(tap(() => updateStoredContactEmail(request.email)));
  }

  /** Renueva la sesión (single-flight). Lo usa el interceptor ante un 401 y el inicializador. */
  refresh(): Observable<AuthView> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.api.refresh().pipe(
        tap(auth => this.guardar(auth.customer)),
        finalize(() => (this.refreshInFlight = null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.refreshInFlight;
  }

  /** Limpia el marcador local DE INMEDIATO y revoca en el backend por separado: un logout no queda a medias. */
  logout(): Observable<void> {
    const revocacion = this.api.logout();
    this.clearLocalSession();
    return revocacion;
  }

  clearLocalSession(): void {
    this.restoreAttempted = true;
    this.customerSignal.set(null);
    clearStoredCustomer();
    this.listener?.alCerrarSesion?.();
  }

  private abrirSesion(auth: AuthView, captura: unknown): Observable<LoginOutcome> {
    if (auth.mfaRequired) {
      return of({ auth, avisos: [], captura });
    }
    this.guardar(auth.customer);
    const adopcion = this.listener?.adoptar?.(captura) ?? of([]);
    // La adopción del carrito no debe impedir entrar: si falla, la sesión ya está abierta.
    return adopcion.pipe(
      catchError(() => of([] as string[])),
      map(avisos => ({ auth, avisos }))
    );
  }

  private guardar(customer: CustomerView): void {
    const stored = toStoredCustomer(customer);
    this.customerSignal.set(stored);
    writeStoredCustomer(stored);
  }
}
